// ============================================================================
// nav-config — the single source of truth for the app's information
// architecture (Option D, 2026-06-13).
//
// 24 flat MODULES[] entries collapsed to 12 nav rows: a pinned core, the five
// flat ops modules (the AM's daily drivers), and three hubs — People · Money ·
// Admin — where the audit found sprawl. Both the sidebar (nav-shell) and the
// hub layouts (hub-layout) render from this tree, and PR-3's ⌘K palette will
// read the same role-gated index. Keep this reconciled with the am-copilot
// INTENT_REGISTRY nav index (supabase/functions/am-copilot) — one source.
//
// Storage tables are NOT renamed here; this is a *surface* reframe. e.g. the
// "New Accounts" entry still points at the new_account_sop-backed module.
// ============================================================================

import { DESK_SEATS, FIELD_SEATS, FINANCE_PAGES, FLEET_ADMINS, FULL_AM_AUTHORITY, HIRING_MENU, INCENTIVE_SEATS, INVOICING_MENU, OFFICE_SEATS, PROJECTS_MENU, SUBCONTRACTORS_MENU, SUPERUSER_ONLY, TIME_MENU } from './role-gates';
import {
  BadgeDollarSign,
  LayoutDashboard,
  Building2,
  Car,
  CalendarClock,
  ClipboardCheck,
  FileSignature,
  FileSpreadsheet,
  FolderKanban,
  Gauge,
  History,
  Home as HomeIcon,
  Inbox,
  Megaphone,
  Package,
  Receipt,
  Route as RouteIcon,
  ShieldCheck,
  Sparkles,
  Tag,
  Truck,
  UserPlus,
  Users,
  Workflow,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { UserRole } from './types';

// Every role list below is a named seat list from ./role-gates — the one place
// a sign-in role word is written. FULL_AM_AUTHORITY mirrors
// has_full_am_authority(); DESK_SEATS is Ben's People Desk (2026-10-07, item 818).

/** Badge keys map a nav entry to one of the live "needs my attention" counts
 *  computed in nav-shell. Hubs bubble up the SUM of their children's badges. */
export type BadgeKey = 'myWork' | 'incentive';

export type HubKey = 'people' | 'money' | 'admin';

export interface NavLeaf {
  kind: 'leaf';
  path: string;
  label: string;
  icon: LucideIcon;
  /** Roles that can see the entry. Empty/undefined = all authenticated users. */
  roles?: UserRole[];
  status?: 'live' | 'soon';
  badgeKey?: BadgeKey;
  /** One-line description shown on a hub Overview card. */
  blurb?: string;
  /** Dynamic (DB-resolved) gate on top of the role list. A gated leaf renders
   *  only when the matching NavGates flag is true — used by Sourcing, whose
   *  access is a DB feature flag + allowlist (can_access_sourcing()), not a
   *  static role rule. */
  gate?: 'sourcing';
}

/** Resolved dynamic gates, passed down from components that hold the hooks
 *  (e.g. useCanAccessSourcing). Omitted/false ⇒ the gated leaf stays hidden,
 *  so we fail closed while the RPC is still resolving. */
export interface NavGates {
  sourcing?: boolean;
}

export interface NavHub {
  kind: 'hub';
  key: HubKey;
  basePath: string;
  label: string;
  icon: LucideIcon;
  /** Extra gate on the whole hub (e.g. Admin = full AM authority). A hub is
   *  also hidden automatically when it has zero role-visible children. */
  roles?: UserRole[];
  /** Mono eyebrow rendered on the hub landing page. */
  eyebrow: string;
  /** The hub's own first tab, in place of the generic Overview. Rendered only
   *  for the roles listed; other roles get no index tab and land on their
   *  first visible child. (People's Desk, item 818.) */
  index?: { label: string; roles: UserRole[] };
  children: NavLeaf[];
}

export interface NavDivider {
  kind: 'divider';
}

export type NavNode = NavLeaf | NavHub | NavDivider;

// ---------------------------------------------------------------------------
// The tree. Order is the render order in the sidebar.
// ---------------------------------------------------------------------------

const PEOPLE_HUB: NavHub = {
  kind: 'hub',
  key: 'people',
  basePath: '/people',
  label: 'People',
  icon: Users,
  eyebrow: 'People · everyone who works on TCS accounts',
  // Ben, 2026-10-07 (item 818): Desk · People · Hiring · Onboarding · Subcontractors.
  // The Desk is HR's queue and replaces the generic Overview; People is one
  // directory with one card for everyone and replaces the salaried-only HR tab.
  // Separations is no longer a tab — it is reached from the person card and the
  // Desk's Leaving lane; its routes and deep links are untouched. Account managers
  // keep Hiring · Onboarding · Subcontractors and can still start a hire.
  index: { label: 'Desk', roles: DESK_SEATS },
  children: [
    {
      kind: 'leaf',
      path: '/people/directory',
      label: 'People',
      icon: Users,
      status: 'live',
      roles: DESK_SEATS,
      blurb: 'Everyone TCS works with — salaried, hourly, 1099 and sub owners — one card each.',
    },
    {
      kind: 'leaf',
      path: '/people/hiring',
      label: 'Hiring',
      icon: UserPlus,
      status: 'live',
      roles: HIRING_MENU,
      blurb: 'Hiring request pipeline — 7-stage kanban.',
    },
    {
      kind: 'leaf',
      path: '/people/onboarding',
      label: 'Onboarding',
      icon: ClipboardCheck,
      status: 'live',
      roles: HIRING_MENU,
      blurb: 'Employee onboarding + subcontractor setup board.',
    },
    {
      kind: 'leaf',
      path: '/people/subcontractors',
      label: 'Subcontractors',
      icon: Truck,
      status: 'live',
      roles: SUBCONTRACTORS_MENU,
      blurb: 'My subs, the pipeline, compliance — add a sub, share your application link.',
    },
    // ⛔ The HR tab (/people/hr) and the Separations tab (/people/separations) are
    // GONE from the strip (Ben, 2026-10-07, item 818). Their routes still answer:
    // /people/hr and /people/hr/directory land on the People directory; every
    // separation screen keeps its path and is reached from the person card, the
    // Desk's Leaving lane, the directory's Separated filter, and the My Work /
    // chat deep links the module always used. The nav row was a convenience,
    // never the access path — RLS on employee_separations and
    // separation_checklist_items is what admits a Finance or Supervisor holder.
  ],
};

const MONEY_HUB: NavHub = {
  kind: 'hub',
  key: 'money',
  basePath: '/money',
  label: 'Money',
  icon: BadgeDollarSign,
  eyebrow: 'Money · contracts, billing, and payouts',
  children: [
    {
      kind: 'leaf',
      path: '/money/invoicing',
      label: 'Invoicing',
      icon: Receipt,
      status: 'live',
      roles: INVOICING_MENU,
      blurb: 'Invoice queue and contract-governed drafts.',
    },
    {
      kind: 'leaf',
      path: '/money/contracts',
      label: 'Contracts',
      icon: FileSpreadsheet,
      status: 'live',
      roles: OFFICE_SEATS,
      blurb: 'Client contracts and e-signing.',
    },
    {
      kind: 'leaf',
      path: '/money/envelopes',
      label: 'E-Sign',
      icon: FileSignature,
      status: 'live',
      roles: OFFICE_SEATS,
      blurb: 'Multi-signer envelopes — field placement and routed signing.',
    },
    // 2026-08-21 (TCSD-0102): the Billing Comms leaf is GONE. Renaming it in
    // place was not enough — "AMs will STILL TRY TO USE IT" (Ben). What it did
    // now has three real homes: AM money messages are project lines under
    // Projects & Billing Comms, machine notices are notifications routed to
    // finance, and roster changes are sub-replacement requests that carry the
    // rate. /money/billing-comms redirects there for one release (App.tsx).
    {
      kind: 'leaf',
      path: '/money/recent-changes',
      label: 'Recent Changes',
      icon: History,
      status: 'live',
      roles: FINANCE_PAGES,
      blurb: 'Material changes routed to Finance — subs, rates, terminations, starts.',
    },
    {
      kind: 'leaf',
      path: '/money/incentives',
      label: 'Performance payments',
      icon: BadgeDollarSign,
      status: 'live',
      roles: INCENTIVE_SEATS,
      badgeKey: 'incentive',
      blurb: 'Scope Performance and compliment subcontractor payments.',
    },
    {
      kind: 'leaf',
      path: '/money/rate-proposals',
      label: 'Rate Proposals',
      icon: Tag,
      status: 'live',
      roles: FINANCE_PAGES,
      blurb: 'Rate change approvals.',
    },
    {
      kind: 'leaf',
      path: '/money/kickers',
      label: 'Annual Increases',
      icon: CalendarClock,
      status: 'live',
      roles: FINANCE_PAGES,
      blurb: 'Inflationary kicker queue — validate or defer upcoming increases.',
    },
  ],
};

const ADMIN_HUB: NavHub = {
  kind: 'hub',
  key: 'admin',
  basePath: '/admin',
  label: 'Admin',
  icon: ShieldCheck,
  roles: FULL_AM_AUTHORITY,
  eyebrow: 'Admin · platform operations',
  children: [
    // AM Collect archived 2026-07-02 — nav leaf removed; intake disabled; data retained.
    {
      kind: 'leaf',
      path: '/admin/users',
      label: 'Users',
      icon: ShieldCheck,
      status: 'live',
      // Ben, 2026-10-20 (role-gates Decision 5): superusers only, as
      // provision_user() / disable_user() are. The Admin hub hides itself for
      // anyone with no child left to see.
      roles: SUPERUSER_ONLY,
      blurb: 'Roles and access.',
    },
    {
      kind: 'leaf',
      path: '/admin/feedback',
      label: 'Feedback',
      icon: Megaphone,
      status: 'live',
      roles: SUPERUSER_ONLY,
      blurb: 'Triage in-app feedback submissions.',
    },
  ],
};

export const NAV: NavNode[] = [
  // ----- Pinned core -----
  { kind: 'leaf', path: '/', label: 'Home', icon: HomeIcon, status: 'live' },
  { kind: 'leaf', path: '/copilot', label: 'Ask AI', icon: Sparkles, status: 'live' },
  {
    kind: 'leaf',
    path: '/my-work',
    label: 'My Work',
    icon: Inbox,
    status: 'live',
    badgeKey: 'myWork',
  },
  // Ben, 7 Oct 2026 (option D): the four rows the item-650 build added here are gone.
  // Notification settings live in the account menu (top right). The all-buildings
  // task list is a Tasks view inside My Work. The old Command Center and Pipelines
  // are retired; their old links land on My Work.
  { kind: 'leaf', path: '/dashboard', label: 'Dashboard', icon: Gauge, status: 'live' },

  { kind: 'divider' },

  // ----- Flat ops modules (the daily drivers) -----
  { kind: 'leaf', path: '/account-management', label: 'Account Management', icon: Building2, status: 'live' },
  {
    kind: 'leaf',
    path: '/route',
    label: 'My Route',
    icon: RouteIcon,
    status: 'live',
    roles: FIELD_SEATS,
  },
  { kind: 'leaf', path: '/supply', label: 'Supply', icon: Package, status: 'live' },
  {
    kind: 'leaf',
    path: '/project-work',
    // TCSD-0102, DECIDED by Ben 2026-08-21 — this exact string. The words
    // "Billing Comms" are retained on purpose: it is the label AMs already
    // navigate by, and in-house it means "everything else about money". What
    // was retired is the separate destination, not the vocabulary. Do not
    // shorten this.
    label: 'Projects & Billing Comms',
    icon: FolderKanban,
    status: 'live',
    roles: PROJECTS_MENU,
  },
  {
    kind: 'leaf',
    path: '/fleet',
    label: 'Fleet & Assets',
    icon: Car,
    status: 'live',
    roles: FLEET_ADMINS,
  },
  {
    kind: 'leaf',
    path: '/time',
    label: 'Time & Labor',
    icon: CalendarClock,
    status: 'live',
    // Widened to account managers 2026-09-21. Ben: "AMs (all app users) should
    // be able to see time & labor tables. This is consistent throughout the
    // app." The five base tables now carry the same `app_read` policy the other
    // ten tables in this module already had, so the menu entry leads somewhere
    // real rather than to an empty screen.
    roles: TIME_MENU,
  },
  {
    kind: 'leaf',
    path: '/new-accounts',
    label: 'New Accounts',
    icon: Workflow,
    status: 'live',
    roles: OFFICE_SEATS,
  },
  {
    kind: 'leaf',
    path: '/bids',
    label: 'Bids',
    icon: Tag,
    status: 'live',
    roles: OFFICE_SEATS,
  },

  { kind: 'divider' },

  // ----- Hubs -----
  PEOPLE_HUB,
  MONEY_HUB,
  ADMIN_HUB,

  // The command center (item 701, TCSD-0412) — the build board, read-only.
  // Superuser only, and last on purpose.
  {
    kind: 'leaf',
    path: '/build-room',
    label: 'Command center',
    icon: LayoutDashboard,
    status: 'live',
    roles: SUPERUSER_ONLY,
  },
];

export const HUBS: Record<HubKey, NavHub> = {
  people: PEOPLE_HUB,
  money: MONEY_HUB,
  admin: ADMIN_HUB,
};

// ---------------------------------------------------------------------------
// Role helpers
// ---------------------------------------------------------------------------

export function roleAllows(role: UserRole | undefined, roles?: UserRole[]): boolean {
  if (!roles || roles.length === 0) return true;
  if (!role) return false;
  return roles.includes(role);
}

export function leafVisible(
  role: UserRole | undefined,
  leaf: NavLeaf,
  gates?: NavGates,
): boolean {
  if (leaf.gate && gates?.[leaf.gate] !== true) return false;
  return roleAllows(role, leaf.roles);
}

/** Children of a hub the given role may see. */
export function visibleHubChildren(
  role: UserRole | undefined,
  hub: NavHub,
  gates?: NavGates,
): NavLeaf[] {
  return hub.children.filter((c) => leafVisible(role, c, gates));
}

/** Whether the hub's own first tab (People's Desk) renders for this role. */
export function hubIndexVisible(role: UserRole | undefined, hub: NavHub): boolean {
  if (!hub.index) return false;
  return roleAllows(role, hub.roles) && roleAllows(role, hub.index.roles);
}

/** A hub renders only when its own gate passes AND it has ≥1 visible child. */
export function hubVisible(
  role: UserRole | undefined,
  hub: NavHub,
  gates?: NavGates,
): boolean {
  if (!roleAllows(role, hub.roles)) return false;
  return visibleHubChildren(role, hub, gates).length > 0;
}

/** Flat list of every leaf the role can reach (for the ⌘K palette + tests). */
export function visibleLeaves(role: UserRole | undefined, gates?: NavGates): NavLeaf[] {
  const out: NavLeaf[] = [];
  for (const node of NAV) {
    if (node.kind === 'leaf') {
      if (leafVisible(role, node, gates)) out.push(node);
    } else if (node.kind === 'hub') {
      if (roleAllows(role, node.roles)) out.push(...visibleHubChildren(role, node, gates));
    }
  }
  return out;
}

export interface NavIndexEntry {
  label: string;
  path: string;
  /** Section heading in the palette / sheet. */
  group: string;
  icon: LucideIcon;
  /** Lowercased search haystack. */
  keywords: string;
}

/** Role-gated, flat navigable index used by the ⌘K palette (PR-3). One source
 *  with the sidebar + hubs — every reachable module + hub overview, in order. */
export function navIndex(role: UserRole | undefined, gates?: NavGates): NavIndexEntry[] {
  const out: NavIndexEntry[] = [];
  let seenDivider = false;
  for (const node of NAV) {
    if (node.kind === 'divider') {
      seenDivider = true;
      continue;
    }
    if (node.kind === 'leaf') {
      if (!leafVisible(role, node, gates)) continue;
      out.push({
        label: node.label,
        path: node.path,
        group: seenDivider ? 'Operations' : 'Workspace',
        icon: node.icon,
        keywords: node.label.toLowerCase(),
      });
    } else {
      if (!hubVisible(role, node, gates)) continue;
      if (node.index) {
        // A hub with its own first tab lists that tab for the roles that see
        // it and nothing for the rest — there is no overview to land on.
        if (hubIndexVisible(role, node)) {
          out.push({
            label: `${node.label} ${node.index.label}`,
            path: node.basePath,
            group: node.label,
            icon: node.icon,
            keywords: `${node.label} ${node.index.label} home queue`.toLowerCase(),
          });
        }
      } else {
        out.push({
          label: `${node.label} overview`,
          path: node.basePath,
          group: node.label,
          icon: node.icon,
          keywords: `${node.label} overview home`.toLowerCase(),
        });
      }
      for (const c of visibleHubChildren(role, node, gates)) {
        out.push({
          label: c.label,
          path: c.path,
          group: node.label,
          icon: c.icon,
          keywords: `${c.label} ${node.label} ${c.blurb ?? ''}`.toLowerCase(),
        });
      }
    }
  }
  return out;
}
